import { createTheme } from '@mui/material/styles'

// **MUI styles the admin, never the site.**
//
// This is the one leak worth guarding against, and it is easy to miss
// because the resemblance is so convenient. Structural component types
// express presentational *intent* — a Grid's cell spans, a Container's
// presentation hint, the layout package's spacing scale — which a theme
// maps to rendering, and the platform deliberately holds no CSS. So a
// Grid editor lists cells with their spans as values; it does not lay
// them out in MUI's grid, however close the vocabulary looks.
//
// Nothing in this file may be read by anything that composes or
// describes a page. It dresses the tool.
//
// **It dresses it with an opinion.** What was here before was MUI's
// factory settings plus three heading sizes, and factory settings are
// why the client read as a well-built reference app rather than as a
// product: shouting buttons, forms at a density meant for a marketing
// page, and no answer at all to what a surface, a border or a muted
// label looks like. Every one of those is a decision, every one of them
// belongs in one file, and a screen that wants to differ from them
// should have to say so.
//
// The evidence that this was a real gap rather than a preference: five
// screens had already patched `textTransform: 'none'` onto their own
// buttons by hand, and disagreed about the rest of the button.

/** Neutrals, named once. Borders, muted text and surfaces all come from here. */
const grey = {
  50: '#f8fafb',
  100: '#f1f4f6',
  200: '#e4e9ed',
  300: '#d3dade',
  400: '#a9b4bc',
  500: '#7c8891',
  600: '#5b666e',
  700: '#414a51',
  800: '#2b3238',
  900: '#1a1f23',
}

/**
 * The type scale.
 *
 * Tighter and smaller than MUI's, because this is a tool read at a desk
 * rather than a page read at arm's length, and because a scale with
 * eight steps that differ by two pixels is not a scale. Headings carry
 * negative tracking; body text does not.
 */
const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Helvetica Neue',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: { fontSize: '1.375rem', fontWeight: 650, letterSpacing: '-0.02em', lineHeight: 1.25 },
  h2: { fontSize: '1.0625rem', fontWeight: 650, letterSpacing: '-0.01em', lineHeight: 1.3 },
  h3: { fontSize: '0.9375rem', fontWeight: 650, letterSpacing: '-0.005em', lineHeight: 1.35 },
  subtitle1: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4 },
  subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.4 },
  body1: { fontSize: '0.875rem', lineHeight: 1.55 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
  caption: { fontSize: '0.75rem', lineHeight: 1.45 },
  button: { fontSize: '0.8125rem', fontWeight: 550, letterSpacing: 0 },
  overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.6 },
}

export const theme = createTheme({
  // Both schemes, resolved from the reader's own setting. A tool used
  // all day beside an editor is used in whichever mode that editor is
  // in, and offering only one of them is the tool deciding something
  // that is not its to decide.
  //
  // **Selected by class, not by media query.** `media` reads the
  // operating system and offers no way to disagree with it, which is a
  // setting the reader can see and cannot touch — and one that a
  // screenshot, a projector or a bright room makes wrong. A class is
  // what lets `useColorScheme` hold three states rather than two: the
  // system's answer, and the two ways of overriding it. `system`
  // remains the default, so nothing changes for a reader who never
  // opens the control.
  cssVariables: { colorSchemeSelector: 'class' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#1f5673', light: '#37738f', dark: '#154056', contrastText: '#ffffff' },
        secondary: { main: '#7a4988' },
        success: { main: '#1f7a4d' },
        warning: { main: '#9a6314' },
        error: { main: '#b3261e' },
        info: { main: '#1f5673' },
        grey,
        background: { default: grey[100], paper: '#ffffff' },
        text: { primary: grey[900], secondary: grey[600], disabled: grey[400] },
        divider: grey[200],
        action: { hover: 'rgba(26,31,35,0.04)', selected: 'rgba(31,86,115,0.08)' },
      },
    },
    dark: {
      palette: {
        primary: { main: '#7db4d0', light: '#9ccbe3', dark: '#4e8caa', contrastText: '#0e1518' },
        secondary: { main: '#c79ad2' },
        success: { main: '#5cc08c' },
        warning: { main: '#e0a458' },
        error: { main: '#e5837c' },
        info: { main: '#7db4d0' },
        grey,
        background: { default: '#14191c', paper: '#1b2125' },
        text: { primary: '#e8edf0', secondary: '#9fadb6', disabled: '#65737c' },
        divider: 'rgba(255,255,255,0.10)',
        action: { hover: 'rgba(255,255,255,0.05)', selected: 'rgba(125,180,208,0.14)' },
      },
    },
  },

  typography,
  shape: { borderRadius: 7 },

  components: {
    // A tool's chrome is drawn with borders, not with drop shadows.
    // Elevation is reserved for the things that genuinely float — a
    // menu, a dialog, a tooltip — so that when something does lift, it
    // means something.
    MuiPaper: {
      defaultProps: { variant: 'outlined', elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },

    // **The buttons stop shouting.** MUI uppercases them by default;
    // five screens had already overridden that one at a time, which is
    // the definition of a default in the wrong place.
    MuiButton: {
      defaultProps: { disableElevation: true, size: 'small' },
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 6, paddingInline: 12 },
        sizeSmall: { paddingBlock: 4 },
      },
    },
    MuiToggleButton: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 550 } },
    },
    MuiToggleButtonGroup: {
      styleOverrides: { root: { backgroundColor: 'var(--mui-palette-background-paper)' } },
    },

    // Forms at the density of a tool. 110 text fields shipped at MUI's
    // `medium`, which is ~56px a control before its helper text — a
    // declaration editor built out of them is measured in screens.
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiInputBase: { styleOverrides: { input: { fontSize: '0.8125rem' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: 'var(--mui-palette-divider)' },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.6875rem', marginTop: 4, lineHeight: 1.4 } },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: '0.8125rem' } } },
    MuiCheckbox: { defaultProps: { size: 'small' } },
    MuiRadio: { defaultProps: { size: 'small' } },
    MuiSwitch: { defaultProps: { size: 'small' } },
    MuiIconButton: { defaultProps: { size: 'small' } },

    // Lists and tables are what a tool is mostly made of, so they are
    // dense by default and a screen that wants room says so.
    MuiTable: { defaultProps: { size: 'small' } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: 'var(--mui-palette-divider)', paddingBlock: 8 },
        head: {
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--mui-palette-text-secondary)',
          backgroundColor: 'var(--mui-palette-grey-50)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 6, paddingBlock: 5 } },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: '0.8125rem' },
        secondary: { fontSize: '0.6875rem' },
      },
    },

    // A chip is a label, not a badge. Quiet by default so that a
    // coloured one — "Unpublished changes" — is the one the eye finds.
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: { borderRadius: 5, fontWeight: 500, fontSize: '0.6875rem', height: 21 },
        outlined: { borderColor: 'var(--mui-palette-divider)' },
      },
    },

    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: { borderRadius: 7, fontSize: '0.8125rem', paddingBlock: 6, alignItems: 'center' },
        icon: { paddingBlock: 6 },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 38 },
        indicator: { height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 550,
          fontSize: '0.8125rem',
          minHeight: 38,
          paddingInline: 12,
        },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { fontSize: '0.75rem', fontWeight: 450, paddingBlock: 6, paddingInline: 10 },
      },
    },

    // The three things that genuinely float.
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 10 } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1rem', fontWeight: 650, paddingBottom: 8 } },
    },
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: 8, border: '1px solid var(--mui-palette-divider)' } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: '0.8125rem', paddingBlock: 6 } },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: 'var(--mui-palette-divider)' } } },
    MuiLink: { defaultProps: { underline: 'hover' } },
  },
})
